import { DelayedRender } from '@visiba/react-std';
import { useRef } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { useArrowNavigation } from '../../../utils';
import { useText } from '../../../utils/hooks/use_text';
import { Button } from '../../button';
import { Icon } from '../../icon';
import { useInputComboboxContext } from './_input_combobox.context';
import { InputComboboxRenderItem } from './_input_combobox_render_item.component';
import { theme } from './input_combobox.theme';

export function InputComboboxContent() {
	const {
		loading,
		contentEmptyText,
		optionsToRender,
		multiple,
		onSelectAllOptions,
		onDeselectAllOptions,
		onFilter,
		filterValue,
		readOnly,
	} = useInputComboboxContext();
	const staticText = useText();
	const listOfOptionsToRender = optionsToRender.map((option) => <InputComboboxRenderItem key={option.value} option={option} />);
	const listRef = useRef<HTMLDivElement>(null);
	useArrowNavigation(listRef, { selectors: ['button'], releaseLockAtWrap: 'never' });

	function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
		if (event.key === 'ArrowDown') {
			event.preventDefault();

			if (loading || optionsToRender.length === 0) {
				return;
			}

			setTimeout(() => {
				listRef.current?.querySelector('button')?.focus();
			}, 0);

			return;
		}
	}

	return (
		<elements.content>
			{readOnly ? null : (
				<>
					<elements.contentHead>
						<elements.contentInputIcon>
							<Icon type='magnifyingGlass' size='small' />
						</elements.contentInputIcon>
						<elements.contentInput
							type='text'
							onChange={(event) => onFilter(event.target.value)}
							value={filterValue}
							onKeyDown={handleInputKeyDown}
						/>
					</elements.contentHead>
					{multiple ? (
						<elements.actions>
							<Button variant='ghost' size='xSmall' onClick={onSelectAllOptions} disabled={loading} disabledText=''>
								{staticText('Component.InputCombobox.SelectAll')}
							</Button>
							<Button variant='ghost' size='xSmall' onClick={onDeselectAllOptions} disabled={loading} disabledText=''>
								{staticText('Component.InputCombobox.ClearAll')}
							</Button>
						</elements.actions>
					) : null}
				</>
			)}

			<elements.contentList tabIndex={-1} ref={listRef}>
				{loading ? (
					<DelayedRender>{staticText('Component.InputCombobox.Loading')}</DelayedRender>
				) : (
					<>
						{optionsToRender.length === 0 ? (
							<elements.contentEmpty>{contentEmptyText ?? staticText('Component.InputCombobox.Empty')}</elements.contentEmpty>
						) : (
							listOfOptionsToRender
						)}
					</>
				)}
			</elements.contentList>
		</elements.content>
	);
}

const elements = {
	content: styled.div`
		overflow: hidden;
		border: 1px solid ${theme.getValue('contentBorderColor')};
		border-radius: ${theme.getValue('contentBorderRadius')};
		background: ${theme.getValue('contentBackground')};
		box-shadow: ${theme.getValue('contentBoxShadow')};
		color: ${theme.getValue('contentFontColor')};
		padding: ${coreTokens.spacing.x0_5};
		width: var(--radix-popover-trigger-width);
		display: flex;
		flex-direction: column;
		max-height: min(var(--radix-popover-content-available-height), 600px);
		gap: ${coreTokens.spacing.x0_25};
	`,
	contentHead: styled.div`
		display: flex;
		align-items: center;
		justify-item: space-between;
		padding: ${coreTokens.spacing.x1};
		gap: ${coreTokens.spacing.x1};
		border-bottom: 1px solid ${theme.getValue('inputBorderColor')};
	`,
	contentInputIcon: styled.div`
		color: ${theme.getValue('inputIconColor')};
	`,
	contentInput: styled.input`
		all: unset;
		font: ${theme.getValue('inputFont')};
		height: ${theme.getValue('inputHeight')};
		width: 100%;
	`,
	contentList: styled.div`
		overflow-x: hidden;
		overflow-y: auto;
		height: 100%;
		display: flex;
		flex-direction: column;
		gap: ${coreTokens.spacing.x0_25};
		padding: ${coreTokens.spacing.x0_25};
		margin: calc(${coreTokens.spacing.x0_25} * -1);
		outline: none;
	`,
	contentEmpty: styled.div`
		font: ${theme.getValue('emptyFont')};
		color: ${theme.getValue('emptyFontColor')};
		padding: ${theme.getValue('emptyPadding')};
	`,
	actions: styled.div`
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: ${theme.getValue('actionsGap')};
		background: ${theme.getValue('actionsBackground')};
		padding: ${theme.getValue('actionsPadding')};
	`,
	actionButton: styled.div``,
};
